import {
  CapabilityManifestSchema,
  FOCUS_CONTEXT_ITEM_LIMIT,
  FOCUS_MAX_DECODED_BYTES,
  FOCUS_MAX_TOOL_CALLS,
  canonicalizeJcs,
  type CapabilityManifest,
  type WorkspaceRole,
} from "@mind/contracts/browser";

export const FOCUS_MANIFEST_TTL_MS = 2 * 60_000;

const EDITOR_PROPOSAL_OPERATIONS = [
  "note.create",
  "note.update",
  "note.archive",
  "project.create",
  "project.update",
  "task.create",
  "entity_link.create",
  "entity_link.remove",
  "document.patch",
  "document.replace_from_git",
  "document.replace_recovered",
] as const;

type ContextItem = CapabilityManifest["contextItems"][number];

type ManifestIds = {
  readonly manifestId: string;
  readonly attemptId: string;
};

export type CapabilityManifestInput = ManifestIds & {
  readonly effectiveAccountId: string;
  readonly workspaceId: string;
  readonly workspaceRole: WorkspaceRole;
  readonly contextItems: readonly ContextItem[];
};

export function allowedProposalOperations(workspaceRole: WorkspaceRole): readonly string[] {
  return workspaceRole === "viewer" ? [] : EDITOR_PROPOSAL_OPERATIONS;
}

export function normalizeContextItems(items: readonly ContextItem[]): readonly ContextItem[] {
  const unique = new Set<string>();
  const normalized: ContextItem[] = [];
  for (const item of items) {
    const key = `${item.kind}:${item.id}`;
    if (unique.has(key)) continue;
    if (normalized.length >= FOCUS_CONTEXT_ITEM_LIMIT) {
      throw new RangeError(`Focus context is limited to ${FOCUS_CONTEXT_ITEM_LIMIT} items`);
    }
    unique.add(key);
    normalized.push(item);
  }
  return normalized;
}

export async function createCapabilityManifest(
  input: CapabilityManifestInput,
  subtle: SubtleCrypto = crypto.subtle,
): Promise<CapabilityManifest> {
  const unsigned = {
    manifestId: input.manifestId,
    attemptId: input.attemptId,
    effectiveAccountId: input.effectiveAccountId,
    workspaceIds: [input.workspaceId],
    contextItems: normalizeContextItems(input.contextItems),
    allowedProposalOperations: allowedProposalOperations(input.workspaceRole),
    maxDecodedBytes: FOCUS_MAX_DECODED_BYTES,
    maxToolCalls: FOCUS_MAX_TOOL_CALLS,
  };
  const encoded = new TextEncoder().encode(canonicalizeJcs(unsigned));
  const digestBytes = new Uint8Array(await subtle.digest("SHA-256", encoded));
  const digest = Array.from(digestBytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return CapabilityManifestSchema.parse({ ...unsigned, digest });
}

export function manifestRegistrationExpiry(now: number = Date.now()): string {
  return new Date(now + FOCUS_MANIFEST_TTL_MS).toISOString();
}
